// src/encounter/wrap-up-dismissed-orders.tsx
// Collapsed "Dismissed (N)" group of the wrap-up Pending orders list (PROD-2594). Mirrors pm-react
// `invoice-dismissed-orders.tsx`: who dismissed it, when and why; Restore returns it to Pending.
import { useState } from 'react';
import { Button, StatusChip } from '@scripsteam/scrips-signal-ds';
import { ChevronDown } from 'lucide-react';
import { formatMoney } from '@/shared/lib/money';
import { codeLabel, DISMISS_REASON_LABEL, formatOrderTime, groupFee, type OrderGroup } from '@/billing/lib/pending-orders';

interface WrapUpDismissedOrdersProps {
  groups: OrderGroup[];
  currency: string;
  /** Restore is offered only when the list is actionable. */
  editable: boolean;
  busy: boolean;
  onRestore: (group: OrderGroup) => void;
}

export function WrapUpDismissedOrders({ groups, currency, editable, busy, onRestore }: WrapUpDismissedOrdersProps) {
  const [open, setOpen] = useState(false);
  if (groups.length === 0) return null;

  return (
    <div className="border-t border-border-default pt-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex flex-wrap items-center gap-2 border-0 bg-transparent p-0 text-left text-size-sm font-bold text-text-secondary cursor-pointer"
      >
        <span className={open ? 'inline-flex' : 'inline-flex -rotate-90'} aria-hidden>
          <ChevronDown className="size-4" />
        </span>
        Dismissed ({groups.length})
        <span className="text-size-2xs font-normal text-text-hint">· not billed on this invoice, orders unchanged</span>
      </button>
      {open && (
        <ul className="m-0 mt-2 list-none border-t border-border-subtle p-0">
          {groups.map((g) => {
            const line = g.primary;
            const fee = groupFee(g, {});
            const who = line.dismissedBy?.name;
            const when = formatOrderTime(line.dismissedAt);
            return (
              <li
                key={g.key}
                data-testid="dismissed-order"
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-border-subtle py-2 pl-6 last:border-0"
              >
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-size-sm font-bold text-text-secondary">{line.display}</span>
                    <span className="text-size-2xs tabular-nums text-text-hint">
                      {codeLabel(line)}
                      {fee !== null && ` · ${currency} ${formatMoney(fee)}`}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-size-2xs tabular-nums text-text-hint">
                    {['Dismissed', who, when].filter(Boolean).join(' · ')}
                    {line.dismissReason && (
                      <StatusChip semantic="neutral" tone="soft" size="xs">
                        {DISMISS_REASON_LABEL[line.dismissReason]}
                      </StatusChip>
                    )}
                    {line.dismissNote && <span className="break-words">“{line.dismissNote}”</span>}
                  </div>
                </div>
                {editable && (
                  <Button variant="tertiary" size="sm" disabled={busy} onClick={() => onRestore(g)}>
                    Restore
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
